// Dollars ↔ USD micros and percent ↔ basis points conversion.
//
// The wire format is ALWAYS integer USD micros (1 USD = 1_000_000) and
// integer bps (100 bps = 1%). The operator only ever sees/enters dollars
// and percent — no "micros"/"bps" jargon in the UI.

/** Micros → "$1,234.56". Em dash for null/undefined. */
export function formatUsd(micros: number | null | undefined): string {
    if (micros == null) return "—";
    const dollars = micros / 1_000_000;
    return `$${dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Micros → compact dollars for prose ("$250", "$12.50"). Whole-dollar
 * amounts drop the cents so summary sentences read naturally.
 */
export function formatUsdShort(micros: number | null | undefined): string {
    if (micros == null) return "$…";
    const dollars = micros / 1_000_000;
    if (Number.isInteger(dollars)) return `$${dollars.toLocaleString("en-US")}`;
    return `$${dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Dollar input string → integer micros, or null when not a usable amount.
 * Accepts "$1,250.50", "250", " 19.99 ". Rejects NaN, negatives, empty.
 */
export function parseUsdToMicros(input: string): number | null {
    const cleaned = input.trim().replace(/^\$/, "").replace(/,/g, "");
    if (cleaned === "") return null;
    const n = Number(cleaned);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.round(n * 1_000_000);
}

/** Micros → editable input value ("250" or "19.99"). Empty string for null. */
export function microsToUsdInput(micros: number | null | undefined): string {
    if (micros == null) return "";
    const dollars = micros / 1_000_000;
    return Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
}

/** Bps → "5%" / "12.5%". Em dash for null/undefined. */
export function formatBpsPercent(bps: number | null | undefined): string {
    if (bps == null) return "…%";
    const pct = bps / 100;
    const text = Number.isInteger(pct) ? String(pct) : pct.toFixed(2).replace(/0$/, "");
    return `${text}%`;
}

/**
 * Percent input string → integer bps, or null when invalid.
 * Accepts "5", "5%", "12.5". Rejects NaN, negatives, and > 100%.
 */
export function parsePercentToBps(input: string): number | null {
    const cleaned = input.trim().replace(/%$/, "");
    if (cleaned === "") return null;
    const n = Number(cleaned);
    if (!Number.isFinite(n) || n < 0 || n > 100) return null;
    return Math.round(n * 100);
}

/** Bps → editable input value ("5" or "12.5"). Empty string for null. */
export function bpsToPercentInput(bps: number | null | undefined): string {
    if (bps == null) return "";
    const pct = bps / 100;
    return Number.isInteger(pct) ? String(pct) : String(pct);
}
